import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { WavyDivider } from '../WavyDivider';

/**
 * BLOCK: hero
 * ACF layout name: `block_hero`
 *
 * Fields:
 *  - eyebrow            (text)
 *  - heading            (text, renders as H1)
 *  - subheading         (text, optional)
 *  - body               (textarea, optional)
 *  - image / image_alt  (image)
 *  - align              (select: left | center)
 *  - overlay            (select: dark | light)
 *  - object_position    (text, optional — CSS object-position)
 *  - buttons            (repeater: label, link, style[primary|secondary])
 *  - divider_fill       (colour picker — matches the next section's background)
 *
 * Used on every top-level page. This is the single hero implementation;
 * the previous per-page variants have been consolidated here.
 */

export interface HeroButton {
  label: string;
  to: string;
  style?: 'primary' | 'secondary';
}
interface PageHeroProps {
  eyebrow?: string;
  heading: React.ReactNode;
  subheading?: string;
  body?: string;
  image: string;
  imageAlt: string;
  objectPosition?: string;
  align?: 'left' | 'center';
  overlay?: 'dark' | 'light';
  /** Renders the body copy inside a solid panel for contrast over busy images. */
  bodyPanel?: boolean;
  buttons?: HeroButton[];
  dividerFill?: string;
  /** Vertical rhythm preset. */
  size?: 'standard' | 'tall' | 'compact';
}
const MIN_HEIGHT: Record<NonNullable<PageHeroProps['size']>, string> = {
  compact: 'min-h-[570px] md:min-h-[620px]',
  standard: 'min-h-[670px] md:min-h-[720px]',
  tall: 'min-h-[820px] lg:min-h-[880px]'
};
export function PageHero({
  eyebrow,
  heading,
  subheading,
  body,
  image,
  imageAlt,
  objectPosition = 'center',
  align = 'left',
  overlay = 'dark',
  bodyPanel = false,
  buttons = [],
  dividerFill = '#F8F2E4',
  size = 'standard'
}: PageHeroProps) {
  const isCentered = align === 'center';
  const isLight = overlay === 'light';
  return <section className={`relative overflow-hidden bg-charcoal`} data-id="element-129" data-mp="d9pjddic0oez">
      <img src={image} alt={imageAlt}
    /* Above-the-fold LCP element: load eagerly at high priority. */ loading="eager" fetchPriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover" style={{
      objectPosition
    }} data-id="element-130" data-mp="db8xoykfgpe4" />
      <div aria-hidden="true" className={`absolute inset-0 ${isLight ? 'bg-white/45' : 'bg-charcoal/55'}`} data-id="element-131" data-mp="3vhf0pb64w43y" />

      <div className={`relative mx-auto flex ${MIN_HEIGHT[size]} max-w-7xl px-6 pb-28 pt-32 md:pb-24 lg:px-10 ${isCentered ? 'items-center justify-center text-center' : 'items-end md:items-center'}`} data-id="element-132" data-mp="ryu4b2gih6qs">
        <div className={isCentered ? 'w-full max-w-4xl' : `max-w-2xl p-7 shadow-2xl sm:p-10 md:p-12 ${isLight ? 'bg-cream/95 text-charcoal' : 'border border-cream-100/20 bg-charcoal/90 text-cream'}`} data-id="element-133" data-mp="i8iooingt942">
          {eyebrow && <p className={`text-xs font-medium tracking-[0.26em] uppercase ${isCentered ? 'text-cream-100/90' : isLight ? 'text-sage-700' : 'text-rose'}`} data-id="element-134" data-mp="1n8gutr62djvn">
              {eyebrow}
            </p>}

          <h1 className={`mt-5 font-serif leading-[0.95] ${isCentered ? 'text-5xl text-white drop-shadow-md md:text-7xl' : isLight ? 'text-5xl text-charcoal sm:text-6xl md:text-7xl' : 'text-5xl sm:text-6xl md:text-7xl'}`} data-id="element-135" data-mp="215m748wdfezc">
            {heading}
          </h1>

          {subheading && <p className={`mt-6 font-serif text-2xl italic leading-snug md:text-3xl ${isCentered ? 'mx-auto max-w-2xl text-cream' : 'text-cream'} ${isLight && !isCentered ? '!text-charcoal-50' : ''}`} data-id="element-136" data-mp="2ox15xroxve5u">
              {subheading}
            </p>}

          {body && (bodyPanel ? <p className="mx-auto mt-7 max-w-3xl border border-cream-100/30 bg-charcoal/95 px-6 py-5 text-base font-light leading-relaxed text-white shadow-xl sm:px-8" data-id="element-137" data-mp="3vdg0nausuady">
                {body}
              </p> : <p className={`mt-6 max-w-xl text-base font-light leading-relaxed sm:text-lg ${isLight ? 'text-charcoal-50' : 'text-cream-100/85'} ${isCentered ? 'mx-auto text-cream-100/90' : ''}`} data-id="element-138" data-mp="2ynypigd1rw4v">
                {body}
              </p>)}

          {buttons.length > 0 && <div className={`mt-9 flex flex-col gap-3 sm:flex-row ${isCentered ? 'justify-center' : ''}`} data-id="element-139" data-mp="rvxur8yl7dg6">
              {buttons.map(button => <Link key={button.to + button.label} to={button.to} className={button.style === 'secondary' ? `inline-flex items-center justify-center gap-3 border px-6 py-3.5 text-xs font-medium tracking-[0.16em] uppercase transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${isLight ? 'border-charcoal/50 text-charcoal hover:bg-charcoal hover:text-cream focus:ring-sage-700' : 'border-cream text-cream hover:bg-cream hover:text-charcoal focus:ring-cream focus:ring-offset-charcoal'}` : `inline-flex items-center justify-center gap-3 px-6 py-3.5 text-xs font-medium tracking-[0.16em] uppercase transition-colors focus:outline-none focus:ring-2 focus:ring-sage-700 focus:ring-offset-2 ${isLight ? 'bg-charcoal text-cream hover:bg-sage-700' : 'bg-cream text-charcoal hover:bg-sage-700 hover:text-cream'}`} data-id="element-140" data-mp="vedh0b1h61de">
                  {button.label}
                  <ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" data-id="element-141" data-mp="21ri0vly5pugz" />
                </Link>)}
            </div>}
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10" data-id="element-142" data-mp="1stohtcpekxbr">
        <WavyDivider fill={dividerFill} data-id="element-143" data-mp="1o1ekvcpvewzz" />
      </div>
    </section>;
}